// OptionChainTab — Full option chain page
import React, { useState } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { useOptionChain } from '../../../hooks/useOptionChain';
import OptionChainTable from '../optionChain/OptionChainTable';
import GreeksDisplay from '../optionChain/GreeksDisplay';
import OIChart from '../optionChain/OIChart';
import StraddleBuilder from '../optionChain/StraddleBuilder';
import PayoffChart from '../optionChain/PayoffChart';
import { FaSync } from 'react-icons/fa';

const UNDERLYING_OPTIONS = [
  { label: 'NIFTY', token: 'NIFTY', exchange: 'NFO' },
  { label: 'BANKNIFTY', token: 'BANKNIFTY', exchange: 'NFO' },
  { label: 'FINNIFTY', token: 'FINNIFTY', exchange: 'NFO' },
  { label: 'SENSEX', token: 'SENSEX', exchange: 'BFO' },
];

export default function OptionChainTab() {
  const { connectionStatus, setOrderModal } = useTrading();
  const [underlying, setUnderlying] = useState(UNDERLYING_OPTIONS[0]);
  const [selectedLegs, setSelectedLegs] = useState([]);
  const [view, setView] = useState('chain'); // chain | strategy | payoff

  const {
    strikes, expiryDates, selectedExpiry, setSelectedExpiry,
    pcr, loading, error, refresh, greeks,
  } = useOptionChain(underlying.token, underlying.exchange);

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-sm text-gray-400">Connect to Angel One to view option chain</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {UNDERLYING_OPTIONS.map(opt => (
            <button
              key={opt.token}
              onClick={() => setUnderlying(opt)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                underlying.token === opt.token
                  ? 'bg-blue-50 text-blue-600 border border-blue-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-transparent'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {expiryDates.length > 0 && (
            <select
              value={selectedExpiry || ''}
              onChange={(e) => setSelectedExpiry(e.target.value)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-1.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {expiryDates.map(exp => (
                <option key={exp} value={exp}>{exp}</option>
              ))}
            </select>
          )}
          {pcr && (
            <div className="px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-100">
              <span className="text-[10px] text-gray-400">PCR: </span>
              <span className="text-xs font-semibold text-gray-900">{parseFloat(pcr.pcr || 0).toFixed(2)}</span>
            </div>
          )}
          <button onClick={() => refresh()} className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-gray-50">
            <FaSync className={`text-xs ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex items-center gap-1">
        {['chain', 'strategy', 'payoff'].map(v => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all ${
              view === v ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            {v === 'chain' ? 'Option Chain' : v === 'strategy' ? 'Strategy Builder' : 'Payoff'}
          </button>
        ))}
      </div>

      {/* Error/Loading State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <span className="text-red-600 text-xs">{error}</span>
          <button onClick={() => refresh()} className="text-xs text-red-600 underline hover:text-red-700">Retry</button>
        </div>
      )}
      {loading && strikes.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
          <p className="text-xs text-gray-500">Loading option chain for {underlying.label} ({selectedExpiry})...</p>
        </div>
      )}

      {/* Content */}
      {view === 'chain' && (
        <div className="space-y-4">
          <OptionChainTable
            strikes={strikes}
            onSelect={(strike, type) => {
              setSelectedLegs(prev => [...prev, { strike: strike.strike, type, data: type === 'CE' ? strike.call : strike.put }]);
            }}
            onTrade={(instrument, side) => setOrderModal({ instrument, side })}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <OIChart strikes={strikes} />
            <GreeksDisplay greeks={greeks} strikes={strikes} />
          </div>
        </div>
      )}
      {view === 'strategy' && (
        <StraddleBuilder
          underlying={underlying}
          expiry={selectedExpiry}
          strikes={strikes}
          selectedLegs={selectedLegs}
          setSelectedLegs={setSelectedLegs}
          onTrade={(instrument, side) => setOrderModal({ instrument, side })}
        />
      )}
      {view === 'payoff' && (
        <PayoffChart legs={selectedLegs} spotPrice={0} />
      )}
    </div>
  );
}
